import type { UnwrapNestedRefs } from "vue";
import type { WellProperties } from "@/composables/forms/formTypes";

import { useFlowRates } from "@/composables/scenarioCalculations/useFlowRates";
import { getGasContributions } from "./gasPhase/gasTable";
import { AIR_MOLECULAR_WEIGHT_G_MOL } from "./constants";
import { Ratio } from "../units/percentage";

export type GasPercentages = {
  h2SHydrogenSulfidePercent: number;
  cO2CarbonDioxidePercent: number;
  n2NitrogenPercent: number;
  c1MethanePercent: number;
  c2EthanePercent: number;
  c3PropanePercent: number;
  ic4IsobutenePercent: number;
  nc4ButanePercent: number;
  ic5IsopentanePercent: number;
  nc5BentanePercent: number;
  c6HexanePercent: number;
  c7HeptanePercent: number;
  o2OxygenPercent: number;
  arArgonPercent: number;
};

export type FlowRates = {
  gasFlowRateSCFD: number; // Q_g
  oilFlowRateSTBD: number; // Q_o
  waterFlowRateSTBD: number; // Q_w
  liquidFlowRateSTBD: number; // Q_l
  gasOilRatioSCFSTB: number; // GOR
  gasLiquidRatioSCFSTB: number; // GLR
  waterCutRatio: number; // WC
  waterCutPercentage?: number; // this is unadjusted, straight from well test input
  gasInOil: number;
  gasInWater: number;
  separatedGas: number;
  freeGas: number;
};

export function extrapolateFlowRatesFromRatios(
  liquidFlowRateSTBD: number,
  existingFlowRates: FlowRates
): FlowRates {
  const oilFlowRateSTBD =
    liquidFlowRateSTBD * (1 - existingFlowRates.waterCutRatio);
  const waterFlowRateSTBD =
    liquidFlowRateSTBD * existingFlowRates.waterCutRatio;
  const gasFlowRateSCFD =
    liquidFlowRateSTBD * existingFlowRates.gasLiquidRatioSCFSTB;

  return {
    ...existingFlowRates,
    oilFlowRateSTBD,
    waterFlowRateSTBD,
    gasFlowRateSCFD,
    liquidFlowRateSTBD
  };
}

type ExtrapolationOption = keyof UnwrapNestedRefs<
  ReturnType<typeof useFlowRates>
>;

type ExtrapolationResult = Pick<
  NonNullable<WellProperties>,
  | "well_test_gas_rate_mscf_day"
  | "well_test_oil_rate_stb_day"
  | "well_test_water_rate_stb_day"
>;

export function extrapolateFlowRates(
  wellProperties: WellProperties,
  option: ExtrapolationOption,
  value: number
): Required<ExtrapolationResult> {
  if (wellProperties) {
    const flowRates = useFlowRates(
      wellProperties,
      wellProperties.well_test_liquid_input_type,
      wellProperties.well_test_gas_input_type
    );

    flowRates[option].value = value;

    return {
      well_test_oil_rate_stb_day: flowRates.oilFlowRateStbD.value,
      well_test_water_rate_stb_day: flowRates.waterFlowRateStbD.value,
      well_test_gas_rate_mscf_day: flowRates.gasFlowRateMscfD.value
    };
  }

  return {
    well_test_oil_rate_stb_day: 0,
    well_test_water_rate_stb_day: 0,
    well_test_gas_rate_mscf_day: 0
  };
}

export function getGasMolecularWeight(
  gasPercentages: GasPercentages,
  gasSpecificGravityOverride: number
): number {
  if (gasSpecificGravityOverride > 0) {
    return gasSpecificGravityOverride * AIR_MOLECULAR_WEIGHT_G_MOL;
  } else {
    const gasEntries = getGasContributions(gasPercentages);
    return (
      gasEntries.reduce(
        (sum, current) => sum + Ratio.toThisUnit(current.yM),
        0
      ) * 100
    );
  }
}

export function getGasSpecificGravity(gasMolecularWeight: number) {
  return gasMolecularWeight / AIR_MOLECULAR_WEIGHT_G_MOL;
}
